import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'

import { FooterBar, TertiaryButton, PrimaryButton } from '../../../components'
import { chartWidth, PRIMARY_RADIUS } from '../../../components/RetirementIncomeDonut'
import { MOBILE } from '../../../guided-toolbox/utils/breakpoints'

// Offset the donut so its arc aligns with the surrounding copy.
export const DONUT_PADDING = 3

const DONUT_WIDTH = `${chartWidth(PRIMARY_RADIUS, DONUT_PADDING)}px`

const Content = styled.div`
  width: 100%;
`

const Heading = styled.h1`
  margin: 0;
  color: ${p => p.theme.mediumGray};
  font-size: 38px;
  font-weight: 300;
  line-height: 52px;

  ${MOBILE} {
    max-width: none;
    font-size: 23px;
    font-weight: 400;
    line-height: 24px;
  }
`

const HeadingEmphasis = styled.strong`
  font-weight: 600;

  ${MOBILE} {
    font-weight: 700;
  }
`

const Intro = styled.p`
  margin: 18px 0 0;
  /* Nudge the intro right to align visually with the heading. */
  padding-left: 3.8px;
  color: ${p => p.theme.mediumGray};
  font-size: 18px;
  font-weight: 300;
  line-height: 24px;

  ${MOBILE} {
    margin-top: 16px;

    /* Reduce the nudge to match the smaller mobile heading. */
    padding-left: 2.3px;
  }
`

const GoalBody = styled.div`
  display: grid;
  grid-template-columns: ${DONUT_WIDTH} minmax(0, 1fr);

  /* Keep the layout top-aligned when the retirement value expands. */
  align-items: start;
  column-gap: 80px;
  margin-top: 50px;

  /* Reserve enough desktop height to expand the retirement value without shifting content below. */
  min-height: 210px;

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
    justify-items: center;
    row-gap: 20px;
    column-gap: 0;
    margin-top: 17px;
    padding: 20px;
    border: 2px solid ${p => p.theme.primaryLighter ?? '#d4ebf2'};
    border-radius: 10px;
  }
`

const ChartCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
  max-width: ${DONUT_WIDTH};
`

const ToneLabel = styled.div`
  color: ${p => p.theme.lightestGray};
  font-size: 23px;
  font-weight: 400;
  line-height: 24px;
  text-align: center;
`

const ValuesCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  ${MOBILE} {
    align-items: center;
  }
`

const CouldYouLive = styled.div`
  color: ${p => p.theme.darkestGray};
  font-size: 18px;
  font-weight: 400;
  line-height: 24px;

  ${MOBILE} {
    text-align: center;
  }
`

// Nudge the section right to align visually with the heading above.
const TellMeMoreSection = styled.div`
  margin-top: 32px;
  padding-left: 3.6px;

  /* Leave room for the fixed footer. */
  padding-bottom: 160px;

  ${MOBILE} {
    margin-top: 27px;
    padding-bottom: 100px;
  }
`

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const Footer = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 0 20px 50px;

  /*
   * No fill of its own. A full-width band of white, wherever it started, cut page
   * content off along a straight line that took no notice of the action bar's
   * rounded ends. The bar is opaque instead and carries the fill for everything
   * from its own midline down — see GoalFooterBar.
   *
   * That fill is a child, so it is clipped to this box: full viewport width, and
   * ending at the bottom of the screen.
   */
  background: transparent;
  overflow: hidden;

  /* Delay the footer so it fades in with the page content. */
  animation: ${fadeIn} 300ms ease-out 600ms both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  ${MOBILE} {
    /*
     * Mobile drops the bar's border and radius and stretches the two buttons edge
     * to edge (see FooterBar), so there are no rounded ends to work around and the
     * footer's own fill can be the bar.
     */
    padding: 0 20px 15px;
    background: #ffffff;
  }
`

// Sits above the bar, where the footer has no fill behind it, so it carries its
// own — otherwise a save failure is read over whatever page content happens to be
// scrolled under it. Its padding is also the breathing room the footer's old top
// padding gave, now costing dead space only when there is an error to show.
const SaveError = styled.div`
  padding: 8px 12px;
  border-radius: 8px;
  background: #ffffff;
  color: #e31e27;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
`

const GoalFooterBar = styled(FooterBar)`
  position: relative;
  width: 100%;

  /* Match the page content width: 1200px minus 20px of padding on each side. */
  max-width: 1160px;

  /* Opaque, so the bar's own rounded shape is what hides the page content that
     scrolls under it. */
  background: #ffffff;

  /*
   * The rest of the footer's fill: full-bleed, starting at the bar's midline and
   * running to the bottom of the screen (the Footer clips both).
   *
   * The midline is exactly where the bar is widest — border-radius: 999px on a bar
   * this size resolves to half its height, making it a true pill — so by the time
   * this square-edged fill takes over, the two notches beside the bar's rounded
   * ends have already tapered to nothing. Starting it any higher would slice those
   * notches off along a straight line, which is the whole thing being avoided.
   */
  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    top: 50%;
    left: -100vw;
    right: -100vw;
    height: 100vh;
    background: #ffffff;
  }

  ${MOBILE} {
    /* Let the Footer provide the mobile horizontal padding. */
    padding: 0;

    /* The footer's own fill covers this case. */
    &::before {
      content: none;
    }
  }
`

const ChangeGoalButton = styled(TertiaryButton)`
  white-space: nowrap;

  ${MOBILE} {
    font-size: 16px;
  }
`

const SaveGoalButton = styled(PrimaryButton)`
  ${MOBILE} {
    font-size: 16px;
  }
`

export {
  Content,
  Heading,
  HeadingEmphasis,
  Intro,
  GoalBody,
  ChartCell,
  ToneLabel,
  ValuesCell,
  CouldYouLive,
  TellMeMoreSection,
  Footer,
  GoalFooterBar,
  SaveError,
  ChangeGoalButton,
  SaveGoalButton,
}
